import { useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import {
  ShieldIcon,
  DocIcon,
  UploadIcon,
  BankIcon,
  UserIcon,
  PhoneIcon,
  InfoIcon,
  CheckIcon,
  AlertIcon,
  MenuIcon,
} from "../../components/ui/icons";
import { contractDoc } from "../../lib/config";
import { useGuideContractVersion } from "../../lib/policy-api";
import { ApiError } from "../../lib/api";
import {
  DEFAULT_GUIDE_LANGUAGE,
  TOUR_CITIES,
  registerGuide,
  requestInsuranceUpload,
  uploadInsuranceFile,
  type RegisterGuideInput,
} from "../../lib/guide-api";
import { cn } from "../../lib/cn";
import { INSURANCE_STEP, nextStep, prevStep, wizardSteps, type Step } from "./steps";

// g-02 Onboarding Wizard（認証必須・G-02）。5ステップで契約署名→反社確認→保険証券→銀行→
// 緊急連絡先を収集し、最終ステップで一括して
// POST /guides（consents=契約+反社 / bank / emergencyContacts）→ POST /guides/:id/insurance を実行する。
//
// 保険証券のステップは保険要件が有効なときだけ出す（insuranceRequired / tst-szv6.5）。保留中（既定）は
// 4ステップになり、証券は集めず POST /guides/:id/insurance も呼ばない。並びの定義は steps.ts。
//
// なぜ最終一括か: POST /guides は1回限りの作成（更新エンドポイントなし）。bank・緊急連絡先・同意は
// すべて作成時に渡す必要があるため、全ステップ収集後に1回だけ作成し、その guideId で保険を上げる。
//
// 提出後の画面はここに無い（tst-00cu.5）。提出が成立したら onSubmitted で親（Onboarding.tsx）へ返し、
// 親が審査中ビューへ切り替える。以前は step 6「Complete」をこの中で出していたが、再ログインすると
// また 1 から始まり、提出済みアカウントが再送信して 409 を踏んでいた。ウィザードは「未提出のとき
// だけ描画される」ので、提出済みでここに戻ってくること自体が起きない。

type EmergencyContact = { name: string; phone: string; relationship: string };

/** 提出が成立したときに親へ返す結果。 */
export type SubmittedResult = {
  /** 保険証券まで S3 へ上げられたか。false なら保険は未提出（pending 扱い）。 */
  insuranceSubmitted: boolean;
  /** 保険アップロード失敗時の案内文（登録自体は成立している）。 */
  warning: string | null;
};

type Props = {
  /** 保険証券の提出を求めるか（GET /guides/me/requirements）。false なら保険のステップを出さない。 */
  insuranceRequired: boolean;
  onSubmitted: (result: SubmittedResult) => void;
  /**
   * 409 already_registered_for_account を踏んだ（＝このアカウントは提出済み）。
   * 親は状態を読み直して審査中ビューへ切り替える。再送信は絶対にさせない。
   */
  onAlreadyRegistered: () => void;
};

export function OnboardingWizard({ insuranceRequired, onSubmitted, onAlreadyRegistered }: Props) {
  const { guide } = useAuth();

  const steps = useMemo(() => wizardSteps(insuranceRequired), [insuranceRequired]);
  const [step, setStep] = useState<Step>(1);

  // Step 1: 契約署名
  // 署名する版は台帳（terms_version）が正本。取得できていなければ署名させない
  // — 版が特定できない同意は証跡として成立しない（tst-bak0.1.31）。
  const contractPolicy = useGuideContractVersion();
  const contractVersion = contractPolicy.status === "ready" ? contractPolicy.policy.version : "";
  const [reviewedContract, setReviewedContract] = useState(false);
  const [signature, setSignature] = useState(guide?.name ?? "");
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [signedDate, setSignedDate] = useState(today);

  // Step 2: 反社確認
  const [antiSocialConfirmed, setAntiSocialConfirmed] = useState(false);

  // Step 3: 保険証券（保険要件が保留中は通らない）
  const [insurer, setInsurer] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [file, setFile] = useState<File | null>(null);

  // Step 4: 銀行
  const [bankName, setBankName] = useState("");
  const [bankBranch, setBankBranch] = useState("");
  const [accountType, setAccountType] = useState<"Savings" | "Checking">("Savings");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountHolder, setAccountHolder] = useState("");

  // Step 5: 緊急連絡先
  const [contacts, setContacts] = useState<EmergencyContact[]>([
    { name: "", phone: "", relationship: "" },
  ]);
  // Step 5: 対応エリア（9 都市から 1 つ以上・マッチングの必須条件・tst-szv6.32）
  const [serviceAreas, setServiceAreas] = useState<string[]>([]);

  // 送信
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const canContinue = (s: Step): boolean => {
    switch (s) {
      case 1:
        return reviewedContract && signature.trim().length > 0 && contractVersion !== "";
      case 2:
        return antiSocialConfirmed;
      case INSURANCE_STEP:
        return file != null;
      case 4:
        return bankName.trim().length > 0 && accountNumber.trim().length > 0;
      case 5:
        return contacts.some((c) => c.name.trim() && c.phone.trim()) && serviceAreas.length > 0;
      default:
        return true;
    }
  };

  const goNext = () => setStep((s) => nextStep(s, steps));
  const goBack = () => setStep((s) => prevStep(s, steps));

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    const validContacts = contacts.filter((c) => c.name.trim() && c.phone.trim());
    const input: RegisterGuideInput = {
      fullName: signature.trim(),
      bank: {
        name: bankName.trim() || undefined,
        branch: bankBranch.trim() || undefined,
        accountType,
        accountNumber: accountNumber.trim() || undefined,
        accountHolder: accountHolder.trim() || undefined,
      },
      emergencyContacts: validContacts.map((c) => ({
        name: c.name.trim(),
        phone: c.phone.trim(),
        relationship: c.relationship.trim() || undefined,
      })),
      // v1 は English 固定運用。マッチング候補条件（guide_language に English 必須）を満たすため
      // 明示的に送る（API 側も未指定なら English を既定付与する・tst-00cu.9）。
      languages: [DEFAULT_GUIDE_LANGUAGE],
      serviceAreas,
      consents: [
        // 版は台帳（terms_version）が正本。取得できていなければ署名させないので、ここでは必ず埋まる。
        { documentType: "ContractTerms", documentVersion: contractVersion },
        // 反社の非該当宣言は版管理された公開文書ではない（terms_version に対応 kind が無い）。
        { documentType: "AntiSocialCheck", documentVersion: "v1" },
      ],
    };

    try {
      const { id: guideId } = await registerGuide(input);

      // 保険証券は guide 作成後に presigned PUT。失敗しても登録自体は成立扱い（後で再提出可）。
      // 保険要件が保留中は上げない（ステップ自体が出ないので file は常に null だが、明示して守る）。
      let warning: string | null = null;
      if (insuranceRequired && file) {
        try {
          const { uploadUrl } = await requestInsuranceUpload(guideId, {
            insurer: insurer.trim() || undefined,
            policyNumber: policyNumber.trim() || undefined,
            validUntil: validUntil || undefined,
            contentType: "application/pdf",
          });
          await uploadInsuranceFile(uploadUrl, file);
        } catch (insErr) {
          warning =
            "Your registration is complete, but the insurance certificate could not be uploaded. It is recorded as pending, so you will not be eligible for matching until you re-submit it and an administrator approves it.";
          console.error("insurance upload failed:", insErr);
        }
      }

      onSubmitted({
        insuranceSubmitted: insuranceRequired && file != null && warning === null,
        warning,
      });
    } catch (e) {
      if (
        e instanceof ApiError &&
        e.status === 409 &&
        e.message === "already_registered_for_account"
      ) {
        // このアカウントは提出済み。親が状態を読み直して審査中ビューへ切り替える。
        onAlreadyRegistered();
        return;
      }
      if (e instanceof ApiError && e.status === 409) {
        setSubmitError(
          "This email address is already registered to another guide account. Please contact TST support.",
        );
      } else {
        setSubmitError(
          e instanceof Error ? e.message : "Could not complete registration. Please try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-dvh bg-canvas px-5 py-8">
      <div className="mx-auto w-full max-w-lg">
        {/* g-02 カンプ準拠: ハンバーガー / アカウントアイコン（共通レイアウト未整備のためプレースホルダ表示のみ） */}
        <div className="flex items-center justify-between">
          <button type="button" aria-label="Menu" className="text-ink-soft hover:text-ink">
            <MenuIcon className="size-6" />
          </button>
          <button type="button" aria-label="Account" className="text-ink-soft hover:text-ink">
            <UserIcon className="size-6" />
          </button>
        </div>
        <header className="mt-3 flex items-start justify-between">
          <div>
            <div className="eyebrow mb-1">Tokyo Signature Tours</div>
            <h1 className="text-3xl font-bold tracking-tight text-ink">Onboarding</h1>
            <p className="mt-2 max-w-xs text-sm text-muted">
              Complete the required steps before becoming available for matching.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-pill bg-accent-soft px-3 py-1.5 text-xs font-medium text-accent">
            <ShieldIcon className="size-3.5" />
            Action required
          </span>
        </header>

        {/* 上段: 登録全体の進捗（Application〜Contract〜Approval）。本Webは Contract 段階。 */}
        <Card className="mt-6 p-5">
          <h3 className="text-sm font-semibold text-ink">Guide registration progress</h3>
          <RegistrationProgress current="Contract" />
        </Card>

        {/* 中段: Contract onboarding のサブステップ一覧（保険要件が保留中は保険を除く 4 つ） */}
        <Card className="mt-4 p-5">
          <h3 className="text-sm font-semibold text-ink">Contract onboarding</h3>
          <ul className="mt-3 space-y-0.5">
            {steps.map((s, i) => {
              const state = s.n < step ? "done" : s.n === step ? "current" : "pending";
              return (
                <li key={s.n} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <span
                      className={cn(
                        "flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                        state === "done"
                          ? "bg-success-soft text-success"
                          : state === "current"
                            ? "bg-accent text-white"
                            : "bg-canvas text-faint",
                      )}
                    >
                      {/* 表示番号は並び順（保険を除いたときに 1,2,4,5 と飛ばさない）。 */}
                      {state === "done" ? <CheckIcon className="size-4" /> : i + 1}
                    </span>
                    <span
                      className={cn(
                        "text-sm",
                        state === "pending" ? "text-muted" : "font-medium text-ink",
                      )}
                    >
                      {s.label}
                    </span>
                  </div>
                  <StatePill state={state} />
                </li>
              );
            })}
          </ul>
        </Card>

        {/* 下段: 現在ステップの本体 */}
        <Card className="mt-4 p-5">
          {step === 1 && (
            <StepContractSignature
              reviewed={reviewedContract}
              setReviewed={setReviewedContract}
              signature={signature}
              setSignature={setSignature}
              signedDate={signedDate}
              setSignedDate={setSignedDate}
              contractVersion={contractVersion}
              insuranceRequired={insuranceRequired}
            />
          )}
          {step === 2 && (
            <StepAntiSocial confirmed={antiSocialConfirmed} setConfirmed={setAntiSocialConfirmed} />
          )}
          {insuranceRequired && step === INSURANCE_STEP && (
            <StepInsurance
              insurer={insurer}
              setInsurer={setInsurer}
              policyNumber={policyNumber}
              setPolicyNumber={setPolicyNumber}
              validUntil={validUntil}
              setValidUntil={setValidUntil}
              file={file}
              setFile={setFile}
            />
          )}
          {step === 4 && (
            <StepBank
              bankName={bankName}
              setBankName={setBankName}
              bankBranch={bankBranch}
              setBankBranch={setBankBranch}
              accountType={accountType}
              setAccountType={setAccountType}
              accountNumber={accountNumber}
              setAccountNumber={setAccountNumber}
              accountHolder={accountHolder}
              setAccountHolder={setAccountHolder}
            />
          )}
          {step === 5 && (
            <>
              <StepEmergency contacts={contacts} setContacts={setContacts} />
              <ServiceAreaPicker selected={serviceAreas} setSelected={setServiceAreas} />
            </>
          )}
        </Card>

        {submitError && (
          <p className="mt-4 flex items-center gap-2 text-sm text-danger">
            <AlertIcon className="size-4 shrink-0" />
            {submitError}
          </p>
        )}

        {/* フッタ操作 */}
        <div className="mt-5 flex gap-3">
          <Button
            variant="outline"
            size="block"
            onClick={goBack}
            disabled={step === 1 || submitting}
          >
            Back
          </Button>
          {step < 5 ? (
            <Button size="block" onClick={goNext} disabled={!canContinue(step)}>
              {step === 1 ? "Sign and Continue" : "Continue"}
            </Button>
          ) : (
            <Button
              size="block"
              onClick={() => void submit()}
              disabled={!canContinue(5) || submitting}
            >
              {submitting ? "Submitting…" : "Submit registration"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** 登録全体の 5 段階（Application / Orientation / Interview / Contract / Approval）。 */
export const REGISTRATION_STAGES = [
  "Application",
  "Orientation",
  "Interview",
  "Contract",
  "Approval",
] as const;
export type RegistrationStage = (typeof REGISTRATION_STAGES)[number];

/**
 * 登録全体の進捗。current より前は完了、current は進行中、後ろは未着手。
 * current="done" で全段階完了（Active）。ウィザード（Contract 段階）と審査中ビュー（Approval 段階）で共有する。
 */
export function RegistrationProgress({ current }: { current: RegistrationStage | "done" }) {
  const currentIndex =
    current === "done" ? REGISTRATION_STAGES.length : REGISTRATION_STAGES.indexOf(current);
  return (
    <div className="mt-4 flex items-start justify-between gap-1">
      {REGISTRATION_STAGES.map((label, i, arr) => {
        const state = i < currentIndex ? "done" : i === currentIndex ? "current" : "pending";
        return (
          <div key={label} className="flex flex-1 flex-col items-center text-center">
            <div className="flex w-full items-center">
              <span className={cn("h-px flex-1", i === 0 ? "bg-transparent" : "bg-line")} />
              <span
                className={cn(
                  "flex size-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold",
                  state === "done"
                    ? "border-success bg-paper text-success"
                    : state === "current"
                      ? "border-accent bg-paper text-accent"
                      : "border-line bg-paper text-faint",
                )}
              >
                {state === "done" ? <CheckIcon className="size-4" /> : i + 1}
              </span>
              <span
                className={cn("h-px flex-1", i === arr.length - 1 ? "bg-transparent" : "bg-line")}
              />
            </div>
            <div className="mt-1.5 text-[11px] text-ink">{label}</div>
            <span
              className={cn(
                "mt-1 rounded-pill px-2 py-0.5 text-[10px] font-medium",
                state === "done"
                  ? "bg-success-soft text-success"
                  : state === "current"
                    ? "bg-accent-soft text-accent"
                    : "bg-canvas text-muted",
              )}
            >
              {state === "done" ? "Completed" : state === "current" ? "Current" : "Pending"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function StatePill({ state }: { state: "done" | "current" | "pending" }) {
  const map = {
    done: { text: "Completed", cls: "bg-success-soft text-success" },
    current: { text: "Current", cls: "bg-accent-soft text-accent" },
    pending: { text: "Pending", cls: "bg-canvas text-muted" },
  } as const;
  const { text, cls } = map[state];
  return (
    <span className={cn("rounded-pill px-2.5 py-1 text-[11px] font-medium", cls)}>{text}</span>
  );
}

// ── 各ステップ本体 ─────────────────────────────────────────────────────

const inputClass =
  "h-12 w-full rounded-md border border-line bg-paper px-3 text-[15px] text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30";

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="mb-1.5 block text-sm font-medium text-ink-soft">{children}</label>;
}

function CheckboxRow({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-sm text-ink-soft">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-5 shrink-0 rounded border-line accent-accent"
      />
      <span>{children}</span>
    </label>
  );
}

function InfoNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-5 flex items-start gap-3 rounded-md bg-accent-soft/60 p-4 text-sm text-ink-soft">
      <InfoIcon className="mt-0.5 size-5 shrink-0 text-accent" />
      <div>{children}</div>
    </div>
  );
}

function StepContractSignature({
  reviewed,
  setReviewed,
  signature,
  setSignature,
  signedDate,
  setSignedDate,
  contractVersion,
  insuranceRequired,
}: {
  reviewed: boolean;
  setReviewed: (v: boolean) => void;
  signature: string;
  setSignature: (v: string) => void;
  signedDate: string;
  setSignedDate: (v: string) => void;
  /** 台帳から取れた契約版。空なら署名させない（版が特定できない同意は証跡にならない）。 */
  contractVersion: string;
  /** 保険証券の提出を求めるか。false なら「この後に必要なもの」から保険を外す。 */
  insuranceRequired: boolean;
}) {
  return (
    <div>
      <h2 className="text-lg font-bold text-ink">Contract Signature</h2>
      <p className="mt-1 text-sm text-muted">
        Review the guide contractor agreement and sign electronically.
      </p>
      {contractVersion === "" && (
        <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          We could not load the current contract version. Please reload before signing.
        </p>
      )}

      <div className="mt-4 rounded-md border border-line bg-canvas/60 p-4">
        <div className="flex items-start gap-3">
          <DocIcon className="mt-0.5 size-7 shrink-0 text-muted" />
          <div>
            <div className="text-sm text-muted">Agreement</div>
            <div className="font-semibold text-ink">{contractDoc.title}</div>
            <div className="mt-1 text-xs text-muted">
              {contractDoc.articleCount} articles &nbsp;|&nbsp; Version: {contractVersion || "—"}
            </div>
          </div>
        </div>
        <div className="mt-4 border-t border-line pt-3">
          <div className="text-sm font-semibold text-ink">Key terms</div>
          <ul className="mt-2 space-y-1 text-sm text-ink-soft">
            {contractDoc.keyTerms.map((term) => (
              <li key={term} className="flex gap-2">
                <span className="text-accent">•</span>
                {term}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-4">
        <CheckboxRow checked={reviewed} onChange={setReviewed}>
          I have reviewed the guide contractor agreement.
        </CheckboxRow>
      </div>

      <div className="mt-4">
        <FieldLabel>Signature (type full name)</FieldLabel>
        <div className="relative">
          <UserIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
          <input
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="Type full name as signature"
            className={cn(inputClass, "pl-9")}
          />
        </div>
      </div>

      <div className="mt-4">
        <FieldLabel>Signed date</FieldLabel>
        <input
          type="date"
          value={signedDate}
          onChange={(e) => setSignedDate(e.target.value)}
          className={inputClass}
        />
      </div>

      <InfoNote>
        After signing, continue to Anti-social confirmation.{" "}
        {insuranceRequired
          ? "Insurance certificate, bank info, and emergency contact are required before matching eligibility."
          : "Bank info and emergency contact are required before matching eligibility."}
      </InfoNote>
    </div>
  );
}

function StepAntiSocial({
  confirmed,
  setConfirmed,
}: {
  confirmed: boolean;
  setConfirmed: (v: boolean) => void;
}) {
  return (
    <div>
      <h2 className="text-lg font-bold text-ink">Anti-social Confirmation</h2>
      <p className="mt-1 text-sm text-muted">
        Confirm that you have no relationship with anti-social forces (per the guide contractor
        agreement).
      </p>

      <div className="mt-4 rounded-md border border-line bg-canvas/60 p-4 text-sm text-ink-soft">
        <div className="flex items-center gap-2 font-semibold text-ink">
          <ShieldIcon className="size-4 text-accent" />
          Declaration
        </div>
        <p className="mt-2">
          I declare that I am not, and will not become, a member of any anti-social force (organized
          crime group, etc.), and that I have no relationship that should be avoided with such
          forces.
        </p>
      </div>

      <div className="mt-4">
        <CheckboxRow checked={confirmed} onChange={setConfirmed}>
          I confirm the anti-social declaration above.
        </CheckboxRow>
      </div>

      <InfoNote>
        This confirmation is recorded with your contract consent and is required for matching
        eligibility.
      </InfoNote>
    </div>
  );
}

function StepInsurance({
  insurer,
  setInsurer,
  policyNumber,
  setPolicyNumber,
  validUntil,
  setValidUntil,
  file,
  setFile,
}: {
  insurer: string;
  setInsurer: (v: string) => void;
  policyNumber: string;
  setPolicyNumber: (v: string) => void;
  validUntil: string;
  setValidUntil: (v: string) => void;
  file: File | null;
  setFile: (f: File | null) => void;
}) {
  return (
    <div>
      <h2 className="text-lg font-bold text-ink">Insurance Certificate</h2>
      <p className="mt-1 text-sm text-muted">
        Liability insurance is required. Upload your certificate as a PDF.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel>Insurer</FieldLabel>
          <input
            value={insurer}
            onChange={(e) => setInsurer(e.target.value)}
            placeholder="Tokio Marine"
            className={inputClass}
          />
        </div>
        <div>
          <FieldLabel>Policy number</FieldLabel>
          <input
            value={policyNumber}
            onChange={(e) => setPolicyNumber(e.target.value)}
            placeholder="POL-0001"
            className={inputClass}
          />
        </div>
      </div>
      <div className="mt-4">
        <FieldLabel>Valid until</FieldLabel>
        <input
          type="date"
          value={validUntil}
          onChange={(e) => setValidUntil(e.target.value)}
          className={inputClass}
        />
      </div>

      <div className="mt-4">
        <FieldLabel>Certificate (PDF)</FieldLabel>
        <label
          className={cn(
            "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-8 text-center transition",
            file ? "border-success bg-success-soft/40" : "border-line bg-canvas/40 hover:bg-canvas",
          )}
        >
          {file ? (
            <>
              <CheckIcon className="size-6 text-success" />
              <span className="text-sm font-medium text-ink">{file.name}</span>
              <span className="text-xs text-muted">Tap to choose a different file</span>
            </>
          ) : (
            <>
              <UploadIcon className="size-6 text-muted" />
              <span className="text-sm font-medium text-ink">Choose PDF</span>
              <span className="text-xs text-muted">Uploaded securely on submit</span>
            </>
          )}
          <input
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
      </div>

      <InfoNote>
        The certificate is uploaded directly to encrypted storage when you submit. An administrator
        verifies it after submission.
      </InfoNote>
    </div>
  );
}

function StepBank({
  bankName,
  setBankName,
  bankBranch,
  setBankBranch,
  accountType,
  setAccountType,
  accountNumber,
  setAccountNumber,
  accountHolder,
  setAccountHolder,
}: {
  bankName: string;
  setBankName: (v: string) => void;
  bankBranch: string;
  setBankBranch: (v: string) => void;
  accountType: "Savings" | "Checking";
  setAccountType: (v: "Savings" | "Checking") => void;
  accountNumber: string;
  setAccountNumber: (v: string) => void;
  accountHolder: string;
  setAccountHolder: (v: string) => void;
}) {
  return (
    <div>
      <h2 className="flex items-center gap-2 text-lg font-bold text-ink">
        <BankIcon className="size-5 text-muted" />
        Bank Info
      </h2>
      <p className="mt-1 text-sm text-muted">
        Where monthly compensation is paid. Account number is stored masked.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel>Bank name</FieldLabel>
          <input
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="MUFG Bank"
            className={inputClass}
          />
        </div>
        <div>
          <FieldLabel>Branch</FieldLabel>
          <input
            value={bankBranch}
            onChange={(e) => setBankBranch(e.target.value)}
            placeholder="Shibuya"
            className={inputClass}
          />
        </div>
        <div>
          <FieldLabel>Account type</FieldLabel>
          <select
            value={accountType}
            onChange={(e) => setAccountType(e.target.value as "Savings" | "Checking")}
            className={inputClass}
          >
            <option value="Savings">Savings</option>
            <option value="Checking">Checking</option>
          </select>
        </div>
        <div>
          <FieldLabel>Account number</FieldLabel>
          <input
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            inputMode="numeric"
            placeholder="1234567"
            className={inputClass}
          />
        </div>
      </div>
      <div className="mt-4">
        <FieldLabel>Account holder</FieldLabel>
        <input
          value={accountHolder}
          onChange={(e) => setAccountHolder(e.target.value)}
          placeholder="YAMADA TARO"
          className={inputClass}
        />
      </div>
    </div>
  );
}

/** 対応エリア（9 都市から 1 つ以上）。予約の都市と一致するガイドだけがマッチング候補になる。 */
function ServiceAreaPicker({
  selected,
  setSelected,
}: {
  selected: string[];
  setSelected: (v: string[]) => void;
}) {
  const toggle = (city: string) =>
    setSelected(selected.includes(city) ? selected.filter((c) => c !== city) : [...selected, city]);
  return (
    <div className="mt-6 border-t border-line pt-5">
      <h2 className="text-lg font-bold text-ink">Available Areas</h2>
      <p className="mt-1 text-sm text-muted">
        Select every city where you can guide (at least one). You only receive offers for tours in
        the cities you select.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {TOUR_CITIES.map((city) => (
          <CheckboxRow key={city} checked={selected.includes(city)} onChange={() => toggle(city)}>
            {city}
          </CheckboxRow>
        ))}
      </div>
    </div>
  );
}

function StepEmergency({
  contacts,
  setContacts,
}: {
  contacts: EmergencyContact[];
  setContacts: (c: EmergencyContact[]) => void;
}) {
  const update = (i: number, patch: Partial<EmergencyContact>) =>
    setContacts(contacts.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const add = () =>
    contacts.length < 5 && setContacts([...contacts, { name: "", phone: "", relationship: "" }]);
  const remove = (i: number) => setContacts(contacts.filter((_, idx) => idx !== i));

  return (
    <div>
      <h2 className="text-lg font-bold text-ink">Emergency Contact</h2>
      <p className="mt-1 text-sm text-muted">
        At least one contact we can reach in case of an emergency during a tour.
      </p>

      <div className="mt-4 space-y-4">
        {contacts.map((c, i) => (
          <div key={i} className="rounded-md border border-line p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-ink">Contact {i + 1}</span>
              {contacts.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-xs font-medium text-danger hover:underline"
                >
                  Remove
                </button>
              )}
            </div>
            <div className="mt-3 space-y-3">
              <div className="relative">
                <UserIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
                <input
                  value={c.name}
                  onChange={(e) => update(i, { name: e.target.value })}
                  placeholder="Name"
                  className={cn(inputClass, "pl-9")}
                />
              </div>
              <div className="relative">
                <PhoneIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
                <input
                  value={c.phone}
                  onChange={(e) => update(i, { phone: e.target.value })}
                  placeholder="Phone"
                  type="tel"
                  className={cn(inputClass, "pl-9")}
                />
              </div>
              <input
                value={c.relationship}
                onChange={(e) => update(i, { relationship: e.target.value })}
                placeholder="Relationship (e.g. Family)"
                className={inputClass}
              />
            </div>
          </div>
        ))}
      </div>

      {contacts.length < 5 && (
        <button
          type="button"
          onClick={add}
          className="mt-3 text-sm font-medium text-accent hover:underline"
        >
          + Add another contact
        </button>
      )}
    </div>
  );
}
